import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, SecurityContext, inject } from '@angular/core';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { CbreHttpConfig, CbreHttpConfigToken, HttpMethods, HttpStrategies, HttpStreamsService } from '@cbre/http';
import { simpleValueValidation } from '@miq/utils';
import { TIMStatus } from '@core/composers/tim-elements/tim-status.enum';
import { FilterSetResponse } from '@records/records-search/shared/search-filters.model';
import { ColloquialAttributes, IOption } from '@shared/models/icommons.model';
import { ColumnSimpleKeys } from '@shared/utils/helpers/catalog.helpers';
import { CatalogsService, CatalogMethod, Column } from '@miq/catalog';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { compose } from '@shared/utils/helpers/compose';
import { EntityType } from '@shared/models/entity-enum';
import { RuleDictionary } from '@shared/models/rules.model';
import { DomSanitizer } from '@angular/platform-browser';

export interface ColumnsQueryParams {
  catalogId: number | string;
  localeId: number | string;
}

@Injectable({
  providedIn: 'root',
})
export class LookupRefService extends HttpMethods {
  private readonly logger: CbreLogger = inject(CbreLoggerToken);
  private tokenReplacer = compose(url => {
    const { id = -1 } = this.userInfoResolver.resolve();
    return url.replace(/:userId/g, id.toString());
  });

  constructor(
    http: HttpClient,
    @Inject(CbreHttpConfigToken) options: CbreHttpConfig,
    protected httpStreamService: HttpStreamsService,
    private catalogService: CatalogsService,
    private userInfoResolver: UserInfoResolver,
    private sanitizer: DomSanitizer
  ) {
    super(http, options);
  }

  public query(value): Observable<any[]> {
    const url = `/lookup/ref?query=${value}`;
    return this.httpStreamService.getStream(url);
  }

  public shortQuery(value): Observable<any[]> {
    const url = `/lookup/ref/short?query=${value}`;
    return this.httpStreamService.getStream(url);
  }

  public category(category) {
    const url = `/lookup/ref/country?lookupcategory=${category}`;
    return this.httpStreamService.getStream(url);
  }

  public offMarketReasonLookup(availabilityTypeDesc: string) {
    let params = '';
    if (!!availabilityTypeDesc) {
      params = `?availability_type_desc=${availabilityTypeDesc}`;
    }

    const url = `/lookup/availability/off-market-reason${params}`;
    return this.httpStreamService.getStream(url);
  }

  public dataClasses(): Observable<IOption[]> {
    const valueToLowerCase = (option: IOption) => ({ ...option, key: option.value.toLowerCase() });
    return this.dataClassesIds().pipe(map(dataClasses => dataClasses.map(valueToLowerCase)));
  }

  public dataClassesIds(): Observable<IOption[]> {
    const uri = '/lookup/dataclasses';
    return this.loadDataFor(uri);
  }

  getFlatColumnsFor(
    entity: string,
    filterNonUpdatableColumns = true,
    columnIdentifier: ColumnSimpleKeys = 'field',
    lovsFromCache = false
  ) {
    return this.#flatColumns(columnIdentifier, filterNonUpdatableColumns, undefined, [entity], lovsFromCache).pipe(
      map(res => ({ ...res[entity] }))
    );
  }

  /**
   * @param columnIdentifier
   * @param entities | Dataclass
   * @param queryParams
   * @param filterNonUpdatableColumns | Flag that indicates if we filter out non updatable or creatable columns
   */
  public getFlatColumnsForAndFilterNonUpdatable(
    columnIdentifier: ColumnSimpleKeys = 'field',
    filterNonUpdatableColumns = true,
    queryParams: Partial<ColumnsQueryParams>,
    ...entities: string[]
  ): Observable<{ [entity: string]: { [path: string]: Column } }> {
    return this.#flatColumns(columnIdentifier, filterNonUpdatableColumns, queryParams, entities, false);
  }

  /**
   * Shared implementation behind `getFlatColumnsFor` and `getFlatColumnsForAndFilterNonUpdatable`.
   * @param lovsFromCache | When true, fills column values from `/lookup/wrapper/:entity` first
   * (see `catalogs.service.ts` `fetchAndMapLovsFromCache`); the per-category LOV fetch then only
   * runs for columns the wrapper didn't cover.
   */
  #flatColumns(
    columnIdentifier: ColumnSimpleKeys,
    filterNonUpdatableColumns: boolean,
    queryParams: Partial<ColumnsQueryParams>,
    entities: string[],
    lovsFromCache: boolean
  ): Observable<{ [entity: string]: { [path: string]: Column } }> {
    const query = `${entities.join(',')}`;
    const params = Object.entries(queryParams || {})
      .filter(([, paramVal]) => simpleValueValidation(paramVal))
      .map(([paramKey, value]) => `${paramKey}=${value}`)
      .join('&');
    const resultQuery = [query, params].filter(str => simpleValueValidation(str)).join('&');

    const options = {
      fetchAndMapLovs: true,
      fetchAndMapLovsFromCache: lovsFromCache,
      mapUoms: false,
      tokenReplacer: this.tokenReplacer,
    };
    return this.catalogService.getColumns(resultQuery, CatalogMethod.Default, options).pipe(
      map(sections =>
        entities.reduce((acc, entity) => {
          const entityFlatColumns = (sections[entity] || [])
            .reduce((accInstance, section) => [...accInstance, ...section.columns], [])
            .filter(x => !filterNonUpdatableColumns || (!!x.parent && (x.parent.update || x.parent.create)))
            .reduce((accum, column) => ({ ...accum, [column[columnIdentifier]]: { ...column } }), {});
          return { ...acc, [entity]: entityFlatColumns };
        }, {})
      )
    );
  }

  /**
   * Get roles catalog.
   */
  public getRoles(): Observable<IOption[]> {
    const uri = '/lookup/roles';
    return this.loadDataFor(uri);
  }

  /**
   * Get usertypes catalog.
   */
  public getUserTypes(): Observable<IOption[]> {
    const uri = '/lookup/usertypes';
    return this.loadDataFor(uri);
  }

  /**
   * Get usertypes catalog.
   */
  public getFilters(): Observable<FilterSetResponse> {
    const uri = '/lookup/filters';
    return this.loadDataFor(uri);
  }

  /**
   * Get confidentiality catalog. (numeric key)
   * @returns Observable<Array<{key: number; value: string}>>
   */
  public getUserConfidentialityLevels(): Observable<IOption[]> {
    const uri = '/lookup/user/confidentiality';
    return this.loadDataFor(uri);
  }

  /**
   * Get user statuses catalog.
   */
  public getUserStatuses(): Observable<IOption[]> {
    const uri = '/lookup/statuses/user';
    return this.loadDataFor(uri);
  }

  public getPermissionStatuses(): Observable<IOption[]> {
    const uri = '/lookup/statuses/access';
    return this.loadDataFor(uri);
  }

  /**
   * Get Property Usage Types catalog.
   */
  public getPropertyUsageTypes(): Observable<IOption[]> {
    const uri = '/lookup/propertyusagetypes';
    return this.loadDataFor(uri, true);
  }

  public getCorporatePropertyUsageTypes(): Observable<IOption[]> {
    const uri = '/lookup/ref/codes?query=property_usage';
    return this.loadDataFor(uri, true);
  }

  /**
   * Will return all the period types with key and label
   * and also indicating if the current date set has data related to each of them
   * @returns
   */
  public getPeriodTypesAll(): Observable<IOption[]> {
    const uri = '/lookup/periodtypes/all';
    return this.loadDataFor(uri);
  }

  /**
   * This endpoint only return the quarterly period type
   * it is hardcoded in the backend
   * @returns
   */
  public getPeriodTypes(): Observable<IOption[]> {
    const uri = '/lookup/periodtypes';
    return this.loadDataFor(uri);
  }

  public getAreaunits() {
    const uri = '/lookup/areaunits';
    return this.loadDataFor(uri);
  }

  public getCountyTypes() {
    const uri = '/lookup/counties';
    return this.loadDataFor(uri);
  }

  public getStateTypes() {
    const uri = '/lookup/states';
    return this.loadDataFor(uri);
  }

  public getCountryTypes() {
    const uri = '/lookup/countries';
    return this.loadDataFor(uri);
  }

  public getYNTypes() {
    const uri = '/lookup/yn';
    return this.loadDataFor(uri);
  }

  public getNAICSTypes() {
    const uri = '/lookup/naics';
    return this.loadDataFor(uri);
  }

  /**
   * /lookup/naics
   *
   * @deprecated due to duplication on {@see getNAICSTypes}
   */
  public naics(): Observable<any[]> {
    const uri = '/lookup/naics';
    return this.loadDataFor(uri);
  }

  public getPrefixTypes() {
    const uri = '/lookup/prefixes';
    return this.loadDataFor(uri);
  }

  public getSuffixTypes() {
    const uri = '/lookup/suffixes';
    return this.loadDataFor(uri);
  }

  public getMarketTypes() {
    const uri = '/lookup/markets';
    return this.loadDataFor(uri);
  }

  public getSubmarketTypes() {
    const uri = '/lookup/submarkets';
    return this.loadDataFor(uri);
  }

  public getDistrictTypes() {
    const uri = '/lookup/districts';
    return this.loadDataFor(uri);
  }

  public getMonthTypes() {
    const uri = '/lookup/months';
    return this.loadDataFor(uri);
  }

  public getRegionsTypes() {
    const uri = '/lookup/regions';
    return this.loadDataFor(uri);
  }

  public getNeighborhoodTypes() {
    const uri = '/lookup/neighborhoods';
    return this.loadDataFor(uri);
  }

  public getGlobalRegionTypes() {
    const uri = '/geo/globalregions';
    return this.loadDataFor(uri);
  }

  public getPartyRole(entity: string) {
    const uri = `/lookup/partyrole/${entity}`;
    return this.loadDataFor(uri);
  }

  public getTimDealStatus(timStatus: TIMStatus) {
    return this.loadDataFor('POST:/ref/cascade/tim/status', false, true, [timStatus]);
  }

  public getAreaUoms(localeId: number) {
    const uri = `/lookup/ref/country?lookupcategory=area&locale=${localeId}`;
    return this.loadDataFor(uri);
  }

  public getCurrencyUoms(localeId: number) {
    const uri = `/lookup/ref/country?lookupcategory=currency&locale=${localeId}`;
    return this.loadDataFor(uri);
  }

  /**
   * Returns observable of API response of /lookup/colloquial
   * @param countryId
   * @returns
   */
  public getColloquial(countryId?: string): Observable<{ [key: string]: ColloquialAttributes }> {
    let params = new HttpParams();
    if (countryId != null) {
      params = params.set('id', countryId);
    }
    const uri = `/lookup/colloquial?${params.toString()}`;
    return this.loadDataFor(uri);
  }

  /**
   * Get confidentiality catalog. (key as string)
   * @returns Observable<Array<{key: string; value: string}>>
   */
  getConfidentialityLevels(): Observable<IOption[]> {
    const uri = '/lookup/confidentiality';
    return this.loadDataFor(uri);
  }

  getReportTypes(): Observable<IOption[]> {
    const uri = '/lookup/reporttypes';
    return this.loadDataFor(uri);
  }

  getUserReportTypes(): Observable<IOption[]> {
    const uri = '/lookup/user/reporttypes';
    return this.loadDataFor(uri, false, true);
  }

  getCurrencySymbols(): Observable<IOption[]> {
    const uri = '/lookup/currency/symbols';
    return this.loadDataFor(uri);
  }

  public loadDataFor(uri, throwErr = false, disableCache = false, data?: any): Observable<any> {
    return this.loadFromRequest(this.tokenReplacer(uri), throwErr, disableCache, data);
  }

  public getAvailabilityAmenities(id: number, type: string): Observable<any> {
    const url = `POST:/property/amenities`;
    const payload = {
      id,
      type,
    };
    return this.loadDataFor(url, false, true, payload);
  }

  public getAPropertyTypeAmenities(type: string): Observable<any> {
    const url = `/lookup/amenities?type=${type}`;
    return this.loadDataFor(url);
  }

  public getAvailabilityStatusLOVsByType(type: string): Observable<any> {
    const url = `/lookup/availabilitystatus?type=${type}`;
    return this.loadDataFor(url);
  }

  private loadFromRequest<T>(uri: string, throwErr = false, disableCache = false, payload: any = {}): Observable<T[]> {
    let strategy = HttpStrategies.GET;
    if (['?all=false', 'POST:'].some(token => uri.includes(token))) {
      uri = uri.replace('POST:', '');
      strategy = HttpStrategies.POST;
    }
    return this.httpStreamService.getStream(uri, strategy, disableCache, payload).pipe(
      catchError(error => {
        this.logger.warn(
          `options for ${this.sanitizer.sanitize(SecurityContext.URL, uri)} were not able to be fetched: ${error}`
        );

        if (throwErr) {
          return throwError(error);
        } else {
          return of([]);
        }
      })
    );
  }

  public getAllColumnFilters(): Observable<FilterSetResponse> {
    const uri = '/lookup/allcolumnfilters';
    return this.loadDataFor(uri);
  }

  /**
   * Get Admin catalog.
   */
  public getMoreColumns(query) {
    const url = `/catalog/admincolumns${query ? `?${query}` : ''}`;
    return this.loadDataFor(url);
  }

  public refreshColumnRef(url: string, disableCache: boolean) {
    return this.loadDataFor(url, false, disableCache);
  }

  /**
   * Get watermark labels
   */
  public getWatermark() {
    // TODO: solve cache issues sending new instances of the cache every single time
    const uri = '/lookup/watermarklabel';
    return this.loadDataFor(uri);
  }

  public getEntityRules(entityName: EntityType): Observable<RuleDictionary> {
    const url = `/lookup/rules/${entityName}`;
    return this.loadDataFor(url);
  }

  public getAuditHistoryLookups(attributeName: string): Observable<Record<string, IOption[]>> {
    const uri = `/audit/lookup/ref?column=${attributeName}`;
    return this.loadDataFor(uri);
  }

  public getTagsByLookup(tagsLookup: string, query: string): Observable<{ key: string; value: string }[]> {
    if (!query || query.trim().length === 0 || !tagsLookup || tagsLookup.trim().length === 0) {
      return of([]);
    }
    const querySearch = encodeURIComponent(query.trim());
    const url = `${tagsLookup}&query=${querySearch}`;
    return this.loadDataFor(url, true, true).pipe(catchError(() => of([])));
  }
}
